import React from "react";
import { Button, Tag } from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  buildPermissionsSummary,
  userRoleColumnDisplay,
  isPendingSuperadminInvite,
} from "./createRolesHelpers";

export function getCreateRolesRoleColumns({
  t,
  lang,
  moduleCatalog,
  moduleAccessTypesCatalog,
  onEditRole,
}) {
  return [
    {
      title: t.col_role_name,
      dataIndex: "name",
      key: "name",
      width: "20%",
    },
    {
      title: t.col_permissions,
      key: "permissions",
      ellipsis: true,
      render: (_, record) =>
        buildPermissionsSummary(
          record.accessByModule,
          lang,
          t,
          moduleCatalog,
          moduleAccessTypesCatalog,
        ),
    },
    {
      title: t.col_created,
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (ts) =>
        dayjs(ts).format(
          lang === "ar" ? "YYYY/MM/DD HH:mm" : "MMM D, YYYY HH:mm",
        ),
    },
    {
      title: t.col_actions,
      key: "actions",
      width: 110,
      minWidth: 110,
      align: "center",
      render: (_, record) => (
        <Button
          type="text"
          className="create-roles-row-edit-btn"
          icon={<EditOutlined />}
          aria-label={t.edit_role}
          onClick={() => onEditRole(record)}
        />
      ),
    },
  ];
}

export function getCreateRolesUserColumns({
  t,
  lang,
  marketRoleNameByKey,
  onEditUser,
  onRevokeUser,
  revokingUserId,
}) {
  return [
    {
      title: t.col_user_email,
      dataIndex: "email",
      key: "email",
      width: "32%",
    },
    {
      title: t.col_user_role,
      key: "roleDisplay",
      width: "24%",
      render: (_, record) =>
        userRoleColumnDisplay(record, marketRoleNameByKey, lang),
    },
    {
      title: t.col_user_status,
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (value) => {
        const s = value != null ? String(value).trim().toLowerCase() : "";
        if (!s) return "—";
        const label = t[`user_status_${s}`] ?? s;
        const color =
          s === "active"
            ? "success"
            : s === "pending"
              ? "warning"
              : "default";
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: t.col_created,
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (ts) =>
        ts == null || !Number.isFinite(Number(ts))
          ? "—"
          : dayjs(ts).format(
              lang === "ar" ? "YYYY/MM/DD HH:mm" : "MMM D, YYYY HH:mm",
            ),
    },
    {
      title: t.col_actions,
      key: "userActions",
      width: 150,
      minWidth: 150,
      align: "center",
      render: (_, record) => {
        const canEdit =
          record.apiUserId != null && Number.isFinite(Number(record.apiUserId));
        const canRevoke = canEdit;
        const canCancelInvite = isPendingSuperadminInvite(record);
        const revokeOrCancelLabel = canCancelInvite
          ? t.cancel_pending_invite
          : t.revoke_user;
        const revokeOrCancelAria = canCancelInvite
          ? t.cancel_pending_invite_aria
          : t.revoke_user;
        return (
          <div className="create-roles-user-actions-cell">
            <Button
              type="text"
              className="create-roles-row-edit-btn"
              icon={<EditOutlined />}
              aria-label={t.edit_user}
              onClick={() => onEditUser(record)}
              disabled={Boolean(revokingUserId) || !canEdit}
            />
            <Button
              type="link"
              danger
              className="create-roles-row-revoke-btn"
              loading={revokingUserId === record.id}
              disabled={
                Boolean(revokingUserId) || (!canRevoke && !canCancelInvite)
              }
              aria-label={revokeOrCancelAria}
              onClick={() => onRevokeUser(record)}
            >
              {revokeOrCancelLabel}
            </Button>
          </div>
        );
      },
    },
  ];
}
